'use client'

import { FormRadio, FormRadioGroup, useFormContext } from '@ariakit/react'
import { Fragment, useContext } from 'react'
import styled from 'styled-components'

import { ColorSwatch, fromMd, untilMd } from '@syconium/little-miss-figgy'

import { trackEvent } from '../../../lib/analytics'
import { useTooltipData } from '../../_hooks/useTooltipData'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import { groupColorOptionsByColorGroupAndPricing } from '../../_utils/groupColorOptionsByColorGroup'
import { FormError } from '../form/FormError'
import { PricingRange } from '../pricing/PricingRange.client'
import {
  ColorGroupTooltip,
  ColorGroupTooltipContent,
  ColorGroupTooltipDescription,
  ColorGroupTooltipTitle,
} from '../tooltips/ColorGroupTooltip.client'

import {
  VariantSelectionFieldProps,
  VariantSelectionFieldValueDisplay,
  VariantSelectionFormContext,
  VariantSelectionFormGroupLabel,
  VariantSelectionFormState,
  VariantSelectionFromSoldOutMessage,
  VariantSelectionInvisibleInput,
  VariantSelectionOptionsRow,
} from './VariantSelectionForm'

type VariantSelectionColorFieldProps = VariantSelectionFieldProps & {
  colorGroupLabels?: 'dynamic' | 'enforce'
  priceBucketing?: 'enabled' | 'disabled'
}

export const VariantSelectionColorRadioGroup = ({
  onChange,
  colorGroupLabels = 'dynamic',
  priceBucketing = 'disabled',
}: VariantSelectionColorFieldProps) => {
  const { product, variantGroup, trackingCategory } = useContext(VariantSelectionFormContext)!
  const formContext = useFormContext()
  const translations = useTranslation()
  const value = formContext!.useValue<VariantSelectionFormState['color']>('color')
  const option = product.options.colors?.find(colorOption => colorOption.color.id === value)
  const colorGroups = groupColorOptionsByColorGroupAndPricing(product.options.colors ?? [])
  const renderSoldOutLabel = variantGroup.availability === 'OUT_OF_STOCK'
  const hasASizeInStock = !!product.options.sizes?.find(size => size.outOfStock === false)

  const { colorCategoryTooltips } = useTooltipData()

  return (
    <div>
      <ColorGroupRadioGroup data-testid='color-picker'>
        {colorGroups.map(colorGroup => {
          const displayGenericGroupLabel = colorGroups.length <= 1 && colorGroupLabels === 'dynamic'
          const tooltip = displayGenericGroupLabel
            ? undefined
            : colorCategoryTooltips?.find(t => t.enumValue.name === colorGroup.group.id)
          const buckets =
            priceBucketing === 'disabled' ? [colorGroup.colorOptions] : colorGroup.pricingBuckets
          return (
            <div key={colorGroup.group.id}>
              <VariantSelectionFormGroupLabel>
                {displayGenericGroupLabel
                  ? translations.detailsSection.colorTitle
                  : colorGroup.group.displayValue}
                {tooltip ? (
                  <ColorGroupTooltip
                    placement='bottom-start'
                    content={
                      <ColorGroupTooltipContent
                        title={<ColorGroupTooltipTitle>{tooltip.title}</ColorGroupTooltipTitle>}
                        description={
                          <ColorGroupTooltipDescription>
                            {tooltip.description}
                          </ColorGroupTooltipDescription>
                        }
                      />
                    }
                  />
                ) : null}
                {option?.color.group?.id === colorGroup.group.id ? (
                  <VariantSelectionFieldValueDisplay>
                    {option.color.displayValue}
                    {renderSoldOutLabel ? (
                      <>
                        &nbsp;
                        <VariantSelectionFromSoldOutMessage />
                      </>
                    ) : null}
                  </VariantSelectionFieldValueDisplay>
                ) : null}
              </VariantSelectionFormGroupLabel>

              {buckets.map(colorOptionsBucket => {
                const key = 'id' in colorOptionsBucket ? colorOptionsBucket.id : colorGroup.group.id
                const colorOptions =
                  'colorOptions' in colorOptionsBucket
                    ? colorOptionsBucket.colorOptions
                    : colorOptionsBucket
                const bucketPricing =
                  'pricing' in colorOptionsBucket ? colorOptionsBucket.pricing : null
                return (
                  <Fragment key={key}>
                    {bucketPricing && buckets.length > 1 ? (
                      <SubLabel>
                        <PricingRange
                          {...bucketPricing}
                          enablePercentageOffText
                          renderCompareAtText={() => null}
                        />
                      </SubLabel>
                    ) : null}
                    <VariantSelectionOptionsRow>
                      {colorOptions.map(colorOption => {
                        const isSelected = value === colorOption.color.id
                        const disabled = isSelected
                          ? colorOption.outOfStock && !hasASizeInStock
                          : colorOption.outOfStock

                        return (
                          <FormRadio
                            key={colorOption.color.id}
                            name='color'
                            value={colorOption.color.id}
                            required
                            render={htmlInputProps => (
                              <label
                                htmlFor={htmlInputProps.id}
                                data-color-availability={disabled ? 'unavailable' : 'available'}
                                {...trackEvent({
                                  action: 'click',
                                  category: trackingCategory,
                                  label: 'Color Swatch',
                                  value: colorOption.color.id,
                                })}
                              >
                                <VariantSelectionInvisibleInput
                                  {...htmlInputProps}
                                  aria-label={colorOption.color.displayValue}
                                  onChange={changeEvent => {
                                    htmlInputProps.onChange?.(changeEvent)
                                    if (changeEvent.target.checked) {
                                      onChange?.(colorOption.color.id)
                                    }
                                  }}
                                />
                                <ColorSwatch
                                  hexCodes={colorOption.color.hexCodes ?? []}
                                  swatchUrl={colorOption.color.swatchImage?.url}
                                  selected={isSelected}
                                  unavailable={disabled}
                                  aria-label={colorOption.color.displayValue}
                                  aria-hidden='true'
                                  size='medium'
                                />
                              </label>
                            )}
                          />
                        )
                      })}
                    </VariantSelectionOptionsRow>
                  </Fragment>
                )
              })}
            </div>
          )
        })}
      </ColorGroupRadioGroup>
      <FormError name='color' />
    </div>
  )
}

export type VariantSelectionFromTooltipProps = {
  title: string
  description?: string
}

const ColorGroupRadioGroup = styled(FormRadioGroup)`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(4)};
`

const SubLabel = styled(VariantSelectionFormGroupLabel)`
  margin-top: ${o => o.theme.spacing(2)};
  margin-bottom: ${o => o.theme.spacing(1)};
  display: block;
  ${fromMd} {
    font-weight: ${o => o.theme.typography.headline.micro.fromMd.fontWeight};
  }
  ${untilMd} {
    font-weight: ${o => o.theme.typography.headline.micro.untilMd.fontWeight};
  }
`
